/**
 * useMentionAutocomplete Hook
 * 
 * Handles @mention autocomplete functionality with keyboard navigation
 * and agent fetching. Similar to Instagram/Discord @mention behavior.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../services/api'
import { logger } from '../utils/logger'

export interface Agent {
  id: string
  username: string
  display_name: string
  email: string
  role: string
  status: string
  avatar: string
  is_online: boolean
}

export interface MentionState {
  isVisible: boolean
  searchQuery: string
  position: { top: number; left: number }
  highlightedIndex: number
}

export interface UseMentionAutocompleteOptions {
  conversationId: string
  textareaRef: React.RefObject<HTMLTextAreaElement>
  onMentionSelect: (agent: Agent, mentionText: string) => void
}

export function useMentionAutocomplete({
  conversationId,
  textareaRef,
  onMentionSelect
}: UseMentionAutocompleteOptions) {
  const [agents, setAgents] = useState<Agent[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [mentionState, setMentionState] = useState<MentionState>({
    isVisible: false,
    searchQuery: '',
    position: { top: 0, left: 0 },
    highlightedIndex: 0
  })

  const lastCursorPosition = useRef<number>(0)

  // Fetch available agents for mentions
  const fetchAgents = useCallback(async () => {
    if (!conversationId) return

    try {
      setIsLoading(true)
      logger.info('🔍 Fetching agents for @mention autocomplete', { conversationId })

      const response = await api.get(`/live-chat/conversations/${conversationId}/available-agents`)
      
      if (response.data?.success && response.data?.agents) {
        setAgents(response.data.agents)
        logger.info('✅ Loaded agents for @mention', { count: response.data.agents.length })
      } else {
        logger.warning('⚠️ No agents data in response')
        setAgents([])
      }
    } catch (error) {
      logger.error('❌ Failed to fetch agents for @mention:', error)
      setAgents([])
    } finally {
      setIsLoading(false)
    }
  }, [conversationId])

  // Load agents when conversation changes
  useEffect(() => {
    fetchAgents()
  }, [fetchAgents])

  // Calculate cursor position for dropdown placement
  const getCaretPosition = useCallback(() => {
    if (!textareaRef.current) return { top: 0, left: 0 }

    const textarea = textareaRef.current
    const rect = textarea.getBoundingClientRect()
    
    // Create invisible div to measure text
    const div = document.createElement('div')
    const style = window.getComputedStyle(textarea)
    
    // Copy textarea styles to div
    div.style.position = 'absolute'
    div.style.visibility = 'hidden'
    div.style.whiteSpace = 'pre-wrap'
    div.style.fontSize = style.fontSize
    div.style.fontFamily = style.fontFamily
    div.style.lineHeight = style.lineHeight
    div.style.padding = style.padding
    div.style.border = style.border
    div.style.width = style.width
    
    // Get text up to cursor
    const textUpToCursor = textarea.value.substring(0, textarea.selectionStart)
    div.textContent = textUpToCursor
    
    document.body.appendChild(div)
    
    // Create span for cursor position
    const span = document.createElement('span')
    span.textContent = '|'
    div.appendChild(span)
    
    const spanRect = span.getBoundingClientRect()
    document.body.removeChild(div)
    
    return {
      top: spanRect.top - rect.top + 25, // Add some offset
      left: spanRect.left - rect.left
    }
  }, [textareaRef])

  // Handle textarea input to detect @mentions
  const handleTextareaInput = useCallback((event: React.FormEvent<HTMLTextAreaElement>) => {
    const textarea = event.currentTarget
    const value = textarea.value
    const cursorPos = textarea.selectionStart

    // Store cursor position
    lastCursorPosition.current = cursorPos

    // Find @ symbol before cursor
    const textBeforeCursor = value.substring(0, cursorPos)
    const lastAtIndex = textBeforeCursor.lastIndexOf('@')
    
    if (lastAtIndex === -1) {
      // No @ found, hide dropdown
      setMentionState(prev => ({ ...prev, isVisible: false }))
      return
    }

    // Check if @ is at start or preceded by whitespace
    const charBeforeAt = lastAtIndex === 0 ? ' ' : textBeforeCursor[lastAtIndex - 1]
    const isValidAtPosition = charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0
    
    logger.debug('🎯 @mention position check', {
      lastAtIndex,
      charBeforeAt: charBeforeAt === ' ' ? 'SPACE' : charBeforeAt,
      isValidAtPosition,
      textBeforeCursor: textBeforeCursor.length
    })
    
    if (!isValidAtPosition) {
      logger.debug('🎯 @mention invalid position, hiding dropdown')
      setMentionState(prev => ({ ...prev, isVisible: false }))
      return
    }

    // Extract search query after @
    const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1)
    
    // Check if there's a space after @ (which would end the mention)
    if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
      setMentionState(prev => ({ ...prev, isVisible: false }))
      return
    }

    // Show dropdown with search query
    const position = getCaretPosition()
    
    setMentionState({
      isVisible: true,
      searchQuery: textAfterAt,
      position,
      highlightedIndex: 0
    })

    logger.debug('🎯 @mention detected', {
      searchQuery: textAfterAt,
      position,
      cursorPos
    })
  }, [getCaretPosition])

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!mentionState.isVisible) return

    const filteredAgents = agents.filter(agent => 
      agent.username.toLowerCase().includes(mentionState.searchQuery.toLowerCase()) ||
      agent.display_name.toLowerCase().includes(mentionState.searchQuery.toLowerCase()) ||
      agent.email.toLowerCase().includes(mentionState.searchQuery.toLowerCase())
    )

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setMentionState(prev => ({
          ...prev,
          highlightedIndex: Math.min(prev.highlightedIndex + 1, filteredAgents.length - 1)
        }))
        break

      case 'ArrowUp':
        event.preventDefault()
        setMentionState(prev => ({
          ...prev,
          highlightedIndex: Math.max(prev.highlightedIndex - 1, 0)
        }))
        break

      case 'Enter':
      case 'Tab':
        if (filteredAgents.length > 0) {
          event.preventDefault()
          const selectedAgent = filteredAgents[mentionState.highlightedIndex]
          handleAgentSelect(selectedAgent)
        }
        break

      case 'Escape':
        event.preventDefault()
        setMentionState(prev => ({ ...prev, isVisible: false }))
        break
    }
  }, [mentionState, agents])

  // Handle agent selection
  const handleAgentSelect = useCallback((agent: Agent) => {
    if (!textareaRef.current) return

    const textarea = textareaRef.current
    const value = textarea.value
    const cursorPos = lastCursorPosition.current

    // Find the @ symbol before cursor
    const textBeforeCursor = value.substring(0, cursorPos)
    const lastAtIndex = textBeforeCursor.lastIndexOf('@')
    
    if (lastAtIndex === -1) return

    // Replace @searchQuery with @username
    const beforeMention = value.substring(0, lastAtIndex)
    const afterCursor = value.substring(cursorPos)
    const mentionText = `@${agent.username}`
    
    const newValue = beforeMention + mentionText + ' ' + afterCursor
    const newCursorPos = beforeMention.length + mentionText.length + 1

    // Update textarea value
    textarea.value = newValue
    textarea.setSelectionRange(newCursorPos, newCursorPos)
    
    // Force React to recognize the change by creating proper events
    const changeEvent = new Event('change', { bubbles: true })
    const inputEvent = new Event('input', { bubbles: true })
    
    // Dispatch both events to ensure React state updates
    textarea.dispatchEvent(inputEvent)
    textarea.dispatchEvent(changeEvent)

    // Hide dropdown
    setMentionState(prev => ({ ...prev, isVisible: false }))

    // Callback for parent component with the complete text
    onMentionSelect(agent, newValue)

    logger.info('✅ Agent selected for @mention', {
      agentId: agent.id,
      username: agent.username,
      mentionText,
      finalValue: newValue
    })
  }, [textareaRef, onMentionSelect])

  // Close dropdown
  const closeMentionDropdown = useCallback(() => {
    setMentionState(prev => ({ ...prev, isVisible: false }))
  }, [])

  return {
    agents,
    isLoading,
    mentionState,
    handleTextareaInput,
    handleKeyDown,
    handleAgentSelect,
    closeMentionDropdown,
    refreshAgents: fetchAgents
  }
}